import { useCallback, useEffect, useId, useState } from 'react';
import { useI18n } from '../../i18n';
import type { Strings } from '../../i18n';
import { useAnnounce } from '../../ui/a11y';
import { Dialog } from '../../ui/Dialog';
import { errorText, rpc, type AppError } from '../admin/api';
import { HeadFrame, type HeadHomeData } from './HeadPages';

// Phase 4 of the topic & follow-up check-in revision (spec §12.B, migrations
// 0024/0025/0028): the head of school's approval queue for a school admin's
// custom follow-up questions. Mirrors HeadR2Pages.tsx's playbook
// approve/reject pattern — one list RPC, one boolean-arg decide RPC,
// announce() + reload after every mutation, ghost-reject before
// primary-approve. Approval authority (primary head only, _require_head(true)
// in 0024) isn't a field on head_pending_questions — this reuses
// home.me.is_primary from the HeadFrame shell every page already fetches,
// same as HeadSettings.tsx's grade list.

type PendingQuestion = {
  id: string;
  category_code: string;
  question_text: string;
  severity_weight: 0 | 1 | 2;
  lens: 'think' | 'act' | 'reflect';
  scale_template_code: string;
  direction: 'ascending' | 'descending';
  created_at: string;
};

export function HeadQuestionsPage() {
  const { t } = useI18n();
  return (
    <HeadFrame tab="questions" title={t['r5.hq.title']}>
      {(_ctx, home) => <HeadQuestionsBody home={home} />}
    </HeadFrame>
  );
}

function HeadQuestionsBody({ home }: { home: HeadHomeData }) {
  const { t } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const [rows, setRows] = useState<PendingQuestion[] | null>(null);
  const [err, setErr] = useState<AppError | null>(null);
  const [decide, setDecide] = useState<{ q: PendingQuestion; approve: boolean } | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [dialogErr, setDialogErr] = useState<AppError | null>(null);

  const load = useCallback(async () => {
    const r = await rpc<PendingQuestion[]>('head_pending_questions');
    if (r.error) setErr(r.error);
    else setRows(r.data);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  if (err && !rows) return <p className="notice notice-block" role="alert">{errorText(t, err)}</p>;
  if (!rows) return <p role="status">{t.loading}</p>;

  return (
    <>
      <p className="notice notice-block">{t['r5.hq.hint']}</p>
      {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
      <section className="panel p5" aria-labelledby={`${id}-t`}>
        <h2 className="panel-title" id={`${id}-t`}>{t['r5.hq.title']}</h2>
        {rows.length === 0 && <p className="text-muted">{t['r5.hq.empty']}</p>}
        <ul className="approval-list">
          {rows.map((q) => (
            <li key={q.id} className="approval">
              <p>
                <strong>{areaLabel(t, q.category_code)}</strong> · {scaleLabel(t, q.scale_template_code)}
              </p>
              <p>{q.question_text}</p>
              {home.me.is_primary ? (
                <div className="form-actions">
                  <button type="button" className="btn btn-ghost" onClick={() => setDecide({ q, approve: false })}>
                    {t['h2.reject']}
                  </button>
                  <button type="button" className="btn btn-primary" onClick={() => setDecide({ q, approve: true })}>
                    {t['h2.approve']}
                  </button>
                </div>
              ) : (
                <p className="notice notice-block">{t['r5.hq.primaryDecides']}</p>
              )}
            </li>
          ))}
        </ul>
      </section>
      <Dialog
        open={!!decide}
        title={decide?.approve ? t['r5.hq.approveTitle'] : t['r5.hq.rejectTitle']}
        intro={decide && <p>{decide.q.question_text}</p>}
        submitLabel={decide?.approve ? t['h2.approve'] : t['h2.reject']}
        busy={busy}
        error={dialogErr ? errorText(t, dialogErr) : null}
        onClose={() => {
          setDecide(null);
          setDialogErr(null);
          setReason('');
        }}
        onSubmit={async () => {
          if (!decide) return;
          setBusy(true);
          setDialogErr(null);
          const r = decide.approve
            ? await rpc('head_approve_question', { p_question_id: decide.q.id })
            : await rpc('head_reject_question', { p_question_id: decide.q.id, p_reason: reason || null });
          setBusy(false);
          if (r.error) return setDialogErr(r.error);
          announce(decide.approve ? t['r5.hq.approved'] : t['r5.hq.rejected']);
          setDecide(null);
          setReason('');
          load();
        }}
      >
        {decide && !decide.approve && (
          <div className="field-group">
            <label className="field-label" htmlFor={`${id}-reason`}>{t['r5.hq.reason']}</label>
            <input id={`${id}-reason`} className="field" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
        )}
      </Dialog>
    </>
  );
}

function areaLabel(t: Strings, code: string): string {
  return t[`area.${code}` as 'area.feel'] ?? code;
}
function scaleLabel(t: Strings, code: string): string {
  return t[`scale.${code}` as 'scale.frequency'] ?? code;
}
